import { useToastProvider } from "@deere/fuel-react";
import { useOktaAuth } from "@okta/okta-react";
import { Amplify } from "aws-amplify";
import { events, EventsChannel } from "aws-amplify/api";
import React, {
  createContext,
  ReactElement,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import {
  EventTypeFilter,
  GetEventsResponse,
  ReadStatusFilter,
  ResolveAlertEvent,
} from "../../api/appNotifications/appNotificationInterfaces";
import useAppNotificationApi from "../../api/appNotifications/useAppNotificationApi";
import { ConfigContext } from "../ConfigContext";
import { DealerAccountContext } from "../DealerAccountContext";
import {
  eventFromAppSyncAction,
  setAlertsAndNotificationsFromDomainAction,
  setBroadcastToggleAction,
  setDownloadNotificationsAction,
  setEventsLoadedAction,
  setIsRealTimeUpdatesDownAction,
  setMarkEventsFromDomainAsReadAction,
  toggleSummaryVisibilityAction,
} from "./reducer/action";
import {
  appNotificationContextReducer,
  getInitialState,
  getResolveAlertId,
  isResolveAlertEvent,
} from "./reducer/reducer";
import {
  getAllAlerts,
  getAllNotifications,
  getEventsLoaded,
  hasDownloadNotificationsUpdatedFromStateValue,
  isRealTimeUpdatesDown,
} from "./reducer/selector";
import useNotifications from "../../components/Layout/components/Header/components/Notifications/hooks/useNotifications/useNotifications";
import useJobsDownloadNotifier from "../../hooks/useJobsDownloadNotifier/useJobsDownloadNotifier";
import usePromotionDownloadNotifier from "../../hooks/usePromotionDownloadNotifier/usePromotionDownloadNotifier";
import { CATEGORIES, TOGGLES } from "../../pages/featureToggles/constants";
import { isAuthorizedForMultipleAccounts } from "../../pages/preferences/utils";
import {
  ALL_ACCOUNTS,
  NOTIFICATIONS_TYPE_TO_DELETE,
} from "../../utils/constants";
import { useEffectiveUserId } from "../DelegateContext/DelegateContext";
import { FeatureToggleContextV2 } from "../FeatureToggleContextV2/FeatureToggleContextV2";
import { RBACAccessContext } from "../RBACAccessContext/RBACAccessContext";
import { useUserContext } from "../UserContext/UserContext";

export enum AlertsAndNotificationSource {
  APP_NOTIFICATION_SERVICE = "APP_NOTIFICATION_SERVICE",
  DOWNLOAD_NOTIFIER = "DOWNLOAD_NOTIFIER",
}

export interface AlertsAndNotification<T = any> {
  id: string;
  type: string;
  payload: T;
  isRead: boolean;
  isSummaryExpanded: boolean;
  createdAt: string;
  source: AlertsAndNotificationSource;
  dealerId: string;
}

export interface AppNotificationContextValues {
  alerts: AlertsAndNotification[];
  notifications: AlertsAndNotification[];
  eventsLoaded: boolean;
  isRealTimeUpdatesDown: boolean;
  markAlertsAsRead: (
    alertsToMarkAsRead: { id: string; source: string }[]
  ) => void;
  markNotificationsAsRead: (
    notificationsToMarkAsRead: { id: string; source: string }[]
  ) => void;
  markDownloadNotificationAsExpired: (
    notificationType: string,
    notificationCreatedTimestampInMs: number
  ) => void;
  toggleNotificationSummary: (id: string) => void;
}

export const AppNotificationContext =
  createContext<AppNotificationContextValues>({
    alerts: [],
    notifications: [],
    eventsLoaded: false,
    isRealTimeUpdatesDown: false,
    markAlertsAsRead: () => {},
    markNotificationsAsRead: () => {},
    markDownloadNotificationAsExpired: () => {},
    toggleNotificationSummary: () => {},
  });

const REALTIME_UPDATES_KEEP_ALIVE_BUFFER_IN_MS = 30_000;
const REALTIME_UPDATES_KEEP_ALIVE_INTERVAL_IN_MS = 2_70_000;

export const AppNotificationContextProvider = ({
  children,
}: {
  children: ReactElement | ReactElement[];
}) => {
  const { dealerAccount, selectedAccounts } = useContext(DealerAccountContext);
  const { isCorporateViewEnabled } = useContext(RBACAccessContext);
  const appNotificationServiceApi = useAppNotificationApi();
  const { authState } = useOktaAuth();
  const user = useUserContext();
  const effectiveUserId = useEffectiveUserId();
  const {
    APP_SYNC_URL,
    DELETE_EXPIRED_NOTIFICATIONS,
    ENABLE_D2D_ORDER_REQUEST_NEED_ACTION_ALERT,
    ENABLE_BROADCAST_MESSAGE_NOTIFICATIONS,
    ENABLE_DAILY_SURPLUS_ALERT_AND_NOTIFICATION,
    ENABLE_MULTIPLE_ACCOUNTS,
    ENABLE_CORPORATE_VIEW_DPA_ROLE,
  } = useContext(ConfigContext);
  const { t } = useTranslation(["common", "alertsAndNotifications"]);
  const { isToggleEnabled } = useContext(FeatureToggleContextV2);

  const isBroadcastMessageFTEnabled = isToggleEnabled(
    CATEGORIES.EXTREME_UI,
    TOGGLES.ENABLE_BROADCAST_MESSAGES_NOTIFICATIONS
  );

  const isMultipleAccountsActive =
    isCorporateViewEnabled &&
    isAuthorizedForMultipleAccounts(
      user,
      ENABLE_MULTIPLE_ACCOUNTS,
      ENABLE_CORPORATE_VIEW_DPA_ROLE
    ) &&
    selectedAccounts.length > 1;
  const accountIds = isMultipleAccountsActive
    ? selectedAccounts.map((account) => account.id)
    : [dealerAccount?.id as string];
  const dealerAccountIdForNotificationHooks = isMultipleAccountsActive
    ? ALL_ACCOUNTS
    : (dealerAccount?.id as string);

  const { enqueueToast } = useToastProvider();

  const {
    notifications: downloadNotifications,
    markNotificationsAsRead: markDownloadNotificationsAsRead,
    expireNotification: expireDownloadNotification,
    deleteNotification: deleteDownloadNotification,
  } = useNotifications({
    accountId: dealerAccountIdForNotificationHooks,
    isMenuOpen: false,
  });
  usePromotionDownloadNotifier(dealerAccountIdForNotificationHooks);
  useJobsDownloadNotifier(dealerAccountIdForNotificationHooks);

  const [state, dispatch] = useReducer(
    appNotificationContextReducer,
    getInitialState(
      ENABLE_D2D_ORDER_REQUEST_NEED_ACTION_ALERT,
      ENABLE_BROADCAST_MESSAGE_NOTIFICATIONS && isBroadcastMessageFTEnabled,
      ENABLE_DAILY_SURPLUS_ALERT_AND_NOTIFICATION
    )
  );
  const keepRealTimeUpdatesAliveRef = useRef<ReturnType<typeof setTimeout>>();
  const alertIdsWithSuccessToastRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const enabled =
      ENABLE_BROADCAST_MESSAGE_NOTIFICATIONS && isBroadcastMessageFTEnabled;
    dispatch(setBroadcastToggleAction(enabled));
  }, [isBroadcastMessageFTEnabled, ENABLE_BROADCAST_MESSAGE_NOTIFICATIONS]);

  const selectedAccountIds = useMemo(
    () =>
      selectedAccounts
        .map((account) => account.id)
        .sort()
        .join(","),
    [selectedAccounts]
  );

  function fetchAllEvents() {
    const isBroadcastEnabled =
      ENABLE_BROADCAST_MESSAGE_NOTIFICATIONS && isBroadcastMessageFTEnabled;
    const apiPromise = isBroadcastEnabled
      ? appNotificationServiceApi.getDealerAndUserScopedEvents({
          dealerIds: accountIds,
          eventType: EventTypeFilter.ALL,
          readStatus: ReadStatusFilter.ALL,
          userScopedCategories: [
            "broadcast_message_created",
            "broadcast_message_updated",
          ],
        })
      : appNotificationServiceApi.getAllEvents({
          dealerIds: accountIds,
          eventType: EventTypeFilter.ALL,
          readStatus: ReadStatusFilter.ALL,
        });

    apiPromise
      .then((eventsList) => {
        dispatch(setAlertsAndNotificationsFromDomainAction(eventsList));
      })
      .catch(() => {
        enqueueToast({
          variant: "error",
          message: t("common:header.alertsAndNotifications.loadError"),
          isCloseable: true,
        });
      })
      .finally(() => {
        dispatch(setEventsLoadedAction(true));
      });
  }

  function markDomainNotificationsAsRead(uniqueIds: string[]) {
    const request = mapMarkAsReadRequest(
      uniqueIds,
      EventTypeFilter.NOTIFICATION
    );
    appNotificationServiceApi
      .markEventsAsRead(request)
      .then(() => {
        enqueueToast({
          message:
            uniqueIds.length > 1
              ? t(
                  "alertsAndNotifications:alertsAndNotifications.markAsRead.notifications.success"
                )
              : t(
                  "alertsAndNotifications:alertsAndNotifications.markAsRead.notification.success"
                ),
          variant: "success",
          isCloseable: true,
        });
        dispatch(setMarkEventsFromDomainAsReadAction(uniqueIds));
      })
      .catch(() => {
        enqueueToast({
          message: t(
            "alertsAndNotifications:alertsAndNotifications.markAsRead.notification.error"
          ),
          variant: "error",
          isCloseable: true,
        });
      });
  }

  function claimAlertSuccessToast(alertIds: string[]): string[] {
    const toastedIds = alertIdsWithSuccessToastRef.current;
    const unclaimedIds = alertIds.filter((alertId) => !toastedIds.has(alertId));
    unclaimedIds.forEach((alertId) => toastedIds.add(alertId));
    return unclaimedIds;
  }

  function markDomainAlertsAsRead(uniqueIds: string[]) {
    const request = mapMarkAsReadRequest(uniqueIds, EventTypeFilter.ALERT);
    appNotificationServiceApi
      .markEventsAsRead(request)
      .then(() => {
        const idsToToast = claimAlertSuccessToast(uniqueIds);
        if (idsToToast.length > 0) {
          enqueueToast({
            message:
              idsToToast.length > 1
                ? t(
                    "alertsAndNotifications:alertsAndNotifications.markAsRead.alerts.success"
                  )
                : t(
                    "alertsAndNotifications:alertsAndNotifications.markAsRead.alert.success"
                  ),
            variant: "success",
            isCloseable: true,
          });
        }
        dispatch(setMarkEventsFromDomainAsReadAction(uniqueIds));
      })
      .catch(() => {
        enqueueToast({
          message: t(
            "alertsAndNotifications:alertsAndNotifications.markAsRead.alert.error"
          ),
          variant: "error",
          isCloseable: true,
        });
      });
  }

  function setupRealTimeUpdatesChannel() {
    appNotificationServiceApi
      .createChannel({ accountIds })
      .then(({ ttl }) => {
        dispatch(setIsRealTimeUpdatesDownAction(false));
        let delay = getTimeDelayForKeepAliveApiTrigger(ttl);
        delay = isDelayInvalidForKeepAliveUpdate(delay)
          ? REALTIME_UPDATES_KEEP_ALIVE_INTERVAL_IN_MS
          : delay;
        keepRealTimeUpdatesChannelAlive(delay);
      })
      .catch(() => {
        dispatch(setIsRealTimeUpdatesDownAction(true));
      });
  }

  function isDelayInvalidForKeepAliveUpdate(delay: number) {
    return delay === undefined || Number.isNaN(delay) || delay <= 0;
  }

  function keepRealTimeUpdatesChannelAlive(delay: number) {
    keepRealTimeUpdatesAliveRef.current = setTimeout(() => {
      appNotificationServiceApi
        .keepChannelAlive()
        .then(({ ttl }) => {
          let delayForNextTrigger = getTimeDelayForKeepAliveApiTrigger(ttl);
          delayForNextTrigger = isDelayInvalidForKeepAliveUpdate(
            delayForNextTrigger
          )
            ? REALTIME_UPDATES_KEEP_ALIVE_INTERVAL_IN_MS
            : delayForNextTrigger;
          keepRealTimeUpdatesChannelAlive(delayForNextTrigger);
        })
        .catch(() => {
          dispatch(setIsRealTimeUpdatesDownAction(true));
          stopKeepingRealTimeUpdatesChannelAlive();
        });
    }, delay);
  }

  function stopKeepingRealTimeUpdatesChannelAlive() {
    clearTimeout(keepRealTimeUpdatesAliveRef.current);
  }

  function getTimeDelayForKeepAliveApiTrigger(ttlInSeconds: number) {
    const expiryEpochInMs = ttlInSeconds * 1000;
    const keepAliveTriggerEpochInMs =
      expiryEpochInMs - REALTIME_UPDATES_KEEP_ALIVE_BUFFER_IN_MS;
    const currentEpochInMs = Date.now();
    return keepAliveTriggerEpochInMs - currentEpochInMs;
  }

  function displayToastForResolveAlertEvent(
    event: GetEventsResponse | ResolveAlertEvent
  ) {
    if (
      isResolveAlertEvent(event) &&
      claimAlertSuccessToast([getResolveAlertId(event)]).length > 0
    ) {
      enqueueToast({
        message: t(
          "alertsAndNotifications:alertsAndNotifications.markAsRead.alert.success"
        ),
        variant: "success",
        isCloseable: true,
      });
    }
  }

  useEffect(() => {
    dispatch(setEventsLoadedAction(false));
    fetchAllEvents();
  }, [selectedAccountIds]);

  if (
    hasDownloadNotificationsUpdatedFromStateValue(state, downloadNotifications)
  ) {
    dispatch(setDownloadNotificationsAction(downloadNotifications));
  }

  useEffect(() => {
    function decodeToken(token: string) {
      return {
        toString: () => token,
        payload: {},
      };
    }

    const myTokenProvider = {
      async getTokens() {
        const idToken = decodeToken(authState?.idToken?.idToken ?? "");
        const accessToken = decodeToken(
          authState?.accessToken?.accessToken ?? ""
        );

        return { idToken, accessToken };
      },
    };

    Amplify.configure(
      {
        API: {
          Events: {
            endpoint: APP_SYNC_URL,
            region: "us-east-1",
            defaultAuthMode: "oidc",
          },
        },
      },
      {
        Auth: {
          tokenProvider: myTokenProvider,
        },
      }
    );
  }, []);

  useEffect(() => {
    let channel: EventsChannel;
    const channelUserId = effectiveUserId.toUpperCase();
    const subscribeToEvents = async () => {
      try {
        channel = await events.connect(`/default/event/${channelUserId}`);
        channel.subscribe({
          next: ({
            event,
          }: {
            event: GetEventsResponse | ResolveAlertEvent;
          }) => {
            dispatch(eventFromAppSyncAction(event));
            displayToastForResolveAlertEvent(event);
          },
          error: (err) => {
            console.error("Subscription error:", err);
            dispatch(setIsRealTimeUpdatesDownAction(true));
          },
        });
      } catch (err) {
        console.error("Failed to connect to AppSync events:", err);
        dispatch(setIsRealTimeUpdatesDownAction(true));
      }
    };

    setupRealTimeUpdatesChannel();
    subscribeToEvents().catch((err) => {
      console.error("Unexpected error in subscribeToEvents:", err);
      dispatch(setIsRealTimeUpdatesDownAction(true));
    });

    return () => {
      channel?.close();
      stopKeepingRealTimeUpdatesChannelAlive();
    };
  }, [effectiveUserId, selectedAccountIds]);

  const contextValue: AppNotificationContextValues = useMemo(
    () => ({
      alerts: getAllAlerts(state),
      notifications: getAllNotifications(state),
      eventsLoaded: getEventsLoaded(state),
      isRealTimeUpdatesDown: isRealTimeUpdatesDown(state),
      toggleNotificationSummary: (id: string) =>
        dispatch(toggleSummaryVisibilityAction(id)),
      markAlertsAsRead: (alertsToMarkAsRead) => {
        const appServiceAlertIds: string[] = [];

        alertsToMarkAsRead.forEach(({ id, source }) => {
          if (source === AlertsAndNotificationSource.APP_NOTIFICATION_SERVICE) {
            appServiceAlertIds.push(id);
          }
        });

        if (appServiceAlertIds.length > 0) {
          markDomainAlertsAsRead(appServiceAlertIds);
        }
      },
      markNotificationsAsRead: (notificationsToMarkAsRead) => {
        const downloadNotificationIds: string[] = [];
        const appServiceNotificationIds: string[] = [];

        notificationsToMarkAsRead.forEach(({ id, source }) => {
          if (source === AlertsAndNotificationSource.DOWNLOAD_NOTIFIER) {
            downloadNotificationIds.push(id);
          } else if (
            source === AlertsAndNotificationSource.APP_NOTIFICATION_SERVICE
          ) {
            appServiceNotificationIds.push(id);
          }
        });

        if (downloadNotificationIds.length > 0) {
          markDownloadNotificationsAsRead(downloadNotificationIds);
        }
        if (appServiceNotificationIds.length > 0) {
          markDomainNotificationsAsRead(appServiceNotificationIds);
        }
      },
      markDownloadNotificationAsExpired: (
        notificationType: string,
        notificationCreatedTimestampInMs: number
      ) => {
        const shouldDeleteOnExpire =
          DELETE_EXPIRED_NOTIFICATIONS &&
          NOTIFICATIONS_TYPE_TO_DELETE.includes(notificationType);
        if (shouldDeleteOnExpire) {
          deleteDownloadNotification(notificationCreatedTimestampInMs);
          return;
        }
        expireDownloadNotification(notificationCreatedTimestampInMs);
      },
    }),
    [
      state.appNotificationDomainAlerts,
      state.appNotificationDomainNotifications,
      state.downloadNotifications,
      state.eventsLoaded,
      state.isRealTimeUpdatesDown,
      state.toggles,
    ]
  );

  return (
    <AppNotificationContext.Provider value={contextValue}>
      {children}
    </AppNotificationContext.Provider>
  );
};

function mapMarkAsReadRequest(uniqueIds: string[], type: EventTypeFilter) {
  return uniqueIds.map((uniqueId) => ({
    id: uniqueId.split("#")[0],
    type,
    timestamp: uniqueId.split("#")[1],
    dealerId: uniqueId.split("#")[2],
  }));
}
